import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  IconButton,
  Paper
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { getModelConfigs } from '../services/seedGrpcService.ts';
import { getQueryableAttributes } from '../services/datalakeGrpcService.ts';

const PipelineForm = ({ onPipelineStart, user }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Available models from Seed server
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);

  // Form state - defaults from rpc/example_data_curation_pipeline.py
  const [formData, setFormData] = useState({
    creator: user?.name || '',
    purpose: 'CORN-WHEAT-BATCH-2024-01',
    model_name: 'SEEDDAMAGE-19',
    uuid: '',
    is_final: false,
    is_original: true,
    vendor_name: 'unknown',
    annotation_type: 'semantic_segmentation',
    annotation_format: 'png',
    add_annotations_format: 'png',
    parent_id: '',
    weed_threshold_percentage: 20.0,
    min_weed_instance_width: 30,
    min_weed_instance_height: 30,
    source_pipeline_id: '',
    source_batch_id: '',
    local_directory_path: '',
    save_destination: 'S3 Bucket',
    // Select images criteria - matching exact field names from example
    num_of_images: '2',
    crop_name: 'corn,wheat',
    ignore_crop_names: '',
    crop_farmer: '',
    ignore_crop_farmer: '',
    crop_field: '',
    ignore_crop_field: '',
    collecting_date: '',
    image_representation: '',
    ignore_image_representation: '',
    ignore_camera_view: '',
    ignore_groups: '',
    ignore_all_batches: 'true'
  });

  // Custom parameters for image selection (key-value pairs)
  const [customParams, setCustomParams] = useState([]);

  // Queryable attributes from table schema
  const [queryableAttributes, setQueryableAttributes] = useState([]);
  const [attributesLoading, setAttributesLoading] = useState(true);
  const attributesFetched = useRef(false); // Prevent double fetch in React StrictMode

  // Fetch queryable attributes on mount
  useEffect(() => {
    // Prevent double execution (React StrictMode calls useEffect twice in dev)
    if (attributesFetched.current) return;
    attributesFetched.current = true;

    const fetchAttributes = async () => {
      setAttributesLoading(true);
      try {
        const response = await getQueryableAttributes();
        if (response.success && response.attributesList) {
          setQueryableAttributes(response.attributesList);
          console.log('[PipelineForm] Loaded queryable attributes:', response.attributesList);
        }
      } catch (err) {
        console.error('[PipelineForm] Error fetching queryable attributes:', err);
      } finally {
        setAttributesLoading(false);
      }
    };

    fetchAttributes();
  }, []);

  // Add a new custom parameter row
  const handleAddCustomParam = () => {
    setCustomParams([...customParams, { key: '', value: '' }]);
  };

  // Remove a custom parameter row
  const handleRemoveCustomParam = (index) => {
    setCustomParams(customParams.filter((_, i) => i !== index));
  };

  // Update a custom parameter
  const handleCustomParamChange = (index, field, value) => {
    const updated = [...customParams];
    updated[index][field] = value;
    setCustomParams(updated);
  };

  // Fetch available models from Seed server on mount
  useEffect(() => {
    const fetchModels = async () => {
      setModelsLoading(true);
      try {
        const response = await getModelConfigs();
        const models = response.configsList || [];
        setAvailableModels(models);
        console.log('[PipelineForm] Loaded models:', models);

        // If current model_name doesn't exist in list, use first model as default
        if (models.length > 0) {
          const modelExists = models.some(m => m.modelName === formData.model_name);
          if (!modelExists) {
            setFormData(prev => ({
              ...prev,
              model_name: models[0].modelName
            }));
          }
        }
      } catch (err) {
        console.error('[PipelineForm] Error fetching models:', err);
        setError('Failed to load available models from Seed server');
      } finally {
        setModelsLoading(false);
      }
    };

    fetchModels();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Update creator field when user info becomes available
  useEffect(() => {
    if (user?.name) {
      setFormData(prev => ({
        ...prev,
        creator: user.name
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Build select_images_criteria - exactly as in rpc/example_data_curation_pipeline.py
      const select_images_criteria = {};
      if (formData.num_of_images) select_images_criteria.num_of_images = formData.num_of_images;
      if (formData.crop_name) select_images_criteria.crop_name = formData.crop_name;
      if (formData.ignore_crop_names) select_images_criteria.ignore_crop_names = formData.ignore_crop_names;
      if (formData.crop_farmer) select_images_criteria.crop_farmer = formData.crop_farmer;
      if (formData.ignore_crop_farmer) select_images_criteria.ignore_crop_farmer = formData.ignore_crop_farmer;
      if (formData.crop_field) select_images_criteria.crop_field = formData.crop_field;
      if (formData.ignore_crop_field) select_images_criteria.ignore_crop_field = formData.ignore_crop_field;
      if (formData.collecting_date) select_images_criteria.collecting_date = formData.collecting_date;
      if (formData.image_representation) select_images_criteria.image_representation = formData.image_representation;
      if (formData.ignore_image_representation) select_images_criteria.ignore_image_representation = formData.ignore_image_representation;
      if (formData.ignore_camera_view) select_images_criteria.ignore_camera_view = formData.ignore_camera_view;
      if (formData.ignore_groups) select_images_criteria.ignore_groups = formData.ignore_groups;
      if (formData.ignore_all_batches) select_images_criteria.ignore_all_batches = formData.ignore_all_batches;

      // Add custom parameters (key-value pairs from user)
      customParams.forEach(param => {
        if (param.key && param.value) {
          select_images_criteria[param.key] = param.value;
        }
      });

      // Build pipeline params
      const pipelineParams = {
        creator: formData.creator,
        creator_id: user?.id || '',  // Keycloak user ID (stable identifier)
        purpose: formData.purpose,
        model_name: formData.model_name,
        uuid: formData.uuid,
        is_final: formData.is_final,
        is_original: formData.is_original,
        vendor_name: formData.vendor_name,
        annotation_type: formData.annotation_type,
        annotation_format: formData.annotation_format,
        add_annotations_format: formData.add_annotations_format,
        parent_id: formData.parent_id,
        weed_threshold_percentage: parseFloat(formData.weed_threshold_percentage),
        min_weed_instance_width: parseInt(formData.min_weed_instance_width),
        min_weed_instance_height: parseInt(formData.min_weed_instance_height),
        source_pipeline_id: formData.source_pipeline_id || '',
        source_batch_id: formData.source_batch_id || '',
        local_directory_path: formData.local_directory_path || '',
        save_destination: formData.save_destination || 'S3 Bucket',
        select_images_criteria
      };

      // DEBUG: Log what we're sending
      console.log('[PipelineForm] Sending pipelineParams:', pipelineParams);
      console.log('[PipelineForm] local_directory_path:', pipelineParams.local_directory_path);
      console.log('[PipelineForm] save_destination:', pipelineParams.save_destination);

      // Call parent component to start pipeline via gRPC-Web
      onPipelineStart(pipelineParams);

      // Note: Parent component (AppGrpcWeb) handles the actual gRPC call
      // and manages all state updates via streaming callbacks
    } catch (err) {
      setError(err.error || err.message || 'Failed to build pipeline parameters');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            border: '1px solid rgba(211, 47, 47, 0.2)'
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Basic Information */}
      <Box sx={{
        mb: 4,
        pb: 3,
        borderBottom: '2px solid rgba(26, 188, 156, 0.1)'
      }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3
          }}
        >
          <Box sx={{
            width: 8,
            height: 24,
            borderRadius: 1,
            background: 'linear-gradient(135deg, #1ABC9C 0%, #16A085 100%)'
          }} />
          Basic Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Creator"
              name="creator"
              value={formData.creator}
              InputProps={{
                readOnly: true,
              }}
              helperText="Auto-filled from your account"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(26, 188, 156, 0.08)',
                  transition: 'all 0.2s ease',
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              required
              disabled={modelsLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(26, 188, 156, 0.02)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(26, 188, 156, 0.04)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(26, 188, 156, 0.05)',
                  }
                }
              }}
            >
              <InputLabel>Model Name</InputLabel>
              <Select
                name="model_name"
                value={formData.model_name}
                onChange={handleChange}
                label="Model Name"
              >
                {modelsLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading models...
                  </MenuItem>
                ) : availableModels.length === 0 ? (
                  <MenuItem disabled>No models available</MenuItem>
                ) : (
                  availableModels.map((model) => (
                    <MenuItem key={model.modelName} value={model.modelName}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {model.modelName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Tile Size: {model.tileWidth} × {model.tileHeight}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>
                {modelsLoading ? 'Loading models from Seed server...' : `${availableModels.length} model(s) available`}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Purpose"
              name="purpose"
              value={formData.purpose}
              onChange={handleChange}
              placeholder="Purpose of this curation pipeline"
              multiline
              rows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(26, 188, 156, 0.02)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(26, 188, 156, 0.04)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(26, 188, 156, 0.05)',
                  }
                }
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Image Selection Criteria */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 3,
          borderRadius: '12px !important',
          border: '1px solid rgba(26, 188, 156, 0.15)',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: '0 0 24px 0',
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
          sx={{
            backgroundColor: 'rgba(26, 188, 156, 0.03)',
            borderRadius: '12px',
            '&.Mui-expanded': {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box sx={{
              width: 8,
              height: 24,
              borderRadius: 1,
              background: 'linear-gradient(135deg, #1ABC9C 0%, #16A085 100%)'
            }} />
            Image Selection Criteria
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                type="number"
                label="Number of Images"
                name="num_of_images"
                value={formData.num_of_images}
                onChange={handleChange}
                placeholder="e.g., 10"
                inputProps={{ min: 1, step: 1 }}
                helperText="Number of images to select"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Crop Names"
                name="crop_name"
                value={formData.crop_name}
                onChange={handleChange}
                placeholder="e.g., corn,wheat"
                helperText="Comma-separated crop names"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Crop Farmer"
                name="crop_farmer"
                value={formData.crop_farmer}
                onChange={handleChange}
                placeholder="Farmer name"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Crop Field"
                name="crop_field"
                value={formData.crop_field}
                onChange={handleChange}
                placeholder="Field name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Collecting Date"
                name="collecting_date"
                value={formData.collecting_date}
                onChange={handleChange}
                placeholder="Collection date"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Image Representation"
                name="image_representation"
                value={formData.image_representation}
                onChange={handleChange}
                placeholder="Image representation"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.ignore_all_batches === 'true'}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      ignore_all_batches: e.target.checked ? 'true' : 'false'
                    }))}
                  />
                }
                label="Ignore all existing annotation batches"
              />
            </Grid>

            {/* Custom Parameters Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Custom Parameters
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddCustomParam}
                  sx={{ borderRadius: 2 }}
                >
                  Add Parameter
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add custom key-value pairs for image selection criteria (e.g., column_name: value)
              </Typography>

              {customParams.map((param, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 1,
                    backgroundColor: 'rgba(26, 188, 156, 0.03)',
                    border: '1px solid rgba(26, 188, 156, 0.1)',
                    borderRadius: 2
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Column Name</InputLabel>
                        <Select
                          value={param.key}
                          onChange={(e) => handleCustomParamChange(index, 'key', e.target.value)}
                          label="Column Name"
                          disabled={attributesLoading}
                        >
                          {attributesLoading ? (
                            <MenuItem value="">Loading...</MenuItem>
                          ) : (
                            queryableAttributes.map((attr) => (
                              <MenuItem key={attr} value={attr}>
                                {attr}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Value"
                        placeholder="e.g., top,side"
                        value={param.value}
                        onChange={(e) => handleCustomParamChange(index, 'value', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveCustomParam(index)}
                        sx={{ width: '100%' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}

              {customParams.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                  No custom parameters added. Click "Add Parameter" to add key-value pairs.
                </Typography>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Advanced Options */}
      <Accordion
        sx={{
          mb: 3,
          borderRadius: '12px !important',
          border: '1px solid rgba(26, 188, 156, 0.15)',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: '0 0 24px 0',
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
          sx={{
            backgroundColor: 'rgba(26, 188, 156, 0.03)',
            borderRadius: '12px',
            '&.Mui-expanded': {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box sx={{
              width: 8,
              height: 24,
              borderRadius: 1,
              background: 'linear-gradient(135deg, #1ABC9C 0%, #16A085 100%)'
            }} />
            Advanced Options
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="UUID (optional)"
                name="uuid"
                value={formData.uuid}
                onChange={handleChange}
                placeholder="Leave empty to auto-generate"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vendor Name"
                name="vendor_name"
                value={formData.vendor_name}
                onChange={handleChange}
                placeholder="e.g., precisionai"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Annotation Type"
                name="annotation_type"
                value={formData.annotation_type}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Annotation Format"
                name="annotation_format"
                value={formData.annotation_format}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Parent ID (optional)"
                name="parent_id"
                value={formData.parent_id}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Weed Threshold (%)"
                name="weed_threshold_percentage"
                value={formData.weed_threshold_percentage}
                onChange={handleChange}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                helperText="Percentage threshold for selecting high-weed images"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Weed Width (px)"
                name="min_weed_instance_width"
                value={formData.min_weed_instance_width}
                onChange={handleChange}
                inputProps={{ min: 10, max: 500, step: 1 }}
                helperText="Minimum width for weed instance extraction"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Weed Height (px)"
                name="min_weed_instance_height"
                value={formData.min_weed_instance_height}
                onChange={handleChange}
                inputProps={{ min: 10, max: 500, step: 1 }}
                helperText="Minimum height for weed instance extraction"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_final}
                    onChange={handleChange}
                    name="is_final"
                  />
                }
                label="Is Final"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_original}
                    onChange={handleChange}
                    name="is_original"
                  />
                }
                label="Is Original"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Local Directory Path (optional)"
                name="local_directory_path"
                value={formData.local_directory_path}
                onChange={handleChange}
                helperText="Local directory path for image source (instead of S3)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Save Destination</InputLabel>
                <Select
                  name="save_destination"
                  value={formData.save_destination}
                  onChange={handleChange}
                  label="Save Destination"
                >
                  <MenuItem value="S3 Bucket">S3 Bucket</MenuItem>
                  <MenuItem value="Local Download">Local Download</MenuItem>
                </Select>
                <FormHelperText>Where to save pipeline results (images and metadata)</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Submit Button */}
      <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
          disabled={loading}
          sx={{
            px: 8,
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 700,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #1ABC9C 0%, #16A085 100%)',
            boxShadow: '0px 8px 24px rgba(26, 188, 156, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #16A085 0%, #0E6655 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0px 12px 32px rgba(26, 188, 156, 0.4)',
            },
            '&:active': {
              transform: 'translateY(0px)',
            },
            '&.Mui-disabled': {
              background: 'linear-gradient(135deg, #BDC3C7 0%, #95A5A6 100%)',
              color: 'white',
            }
          }}
        >
          {loading ? 'Starting Pipeline...' : 'Start Pipeline'}
        </Button>
      </Box>
    </Box>
  );
};

export default PipelineForm;
